/**
 * What goes into a plate, as the cook reads it.
 *
 * An ingredient reaches a dish by four roads. The kitchen display used to read
 * two of them, so any dish whose recipe was entered against the recipe
 * catalogue — the source of truth since feature 048, and the road new lines are
 * required to use — reported "nessun ingrediente registrato" while its
 * ingredients sat in the database.
 *
 * The precedence below is not invented here. It mirrors
 * `src/modules/table/services/kitchen/dish-details.service.ts`, which already
 * answers this question for the phone agent and the WhatsApp assistant. Three
 * channels, one answer: a customer asking on the phone and a cook reading the
 * screen must not be told different things about the same plate.
 *
 * Doses are shown, unlike the customer-facing channels. This screen is read by
 * the person cooking the dish.
 */

export type IngredientSource =
  | "catalog"
  | "warehouse"
  | "preparation"
  | "free-text"
  | "dish";

export interface DishIngredientLine {
  name: string;
  /** Quantity and unit as the venue recorded them; null when it recorded none. */
  dose: string | null;
  source: IngredientSource;
}

export type DishIngredients =
  | { kind: "recorded"; lines: DishIngredientLine[] }
  /**
   * Nothing is on file. Deliberately not an empty `lines` array: the caller
   * must say so in words. An empty list reads as "there is nothing in it".
   */
  | { kind: "nothing-recorded" };

/** A number the way an Italian kitchen writes it, without trailing zeros. */
function formatNumber(value: number): string {
  return value
    .toFixed(3)
    .replace(/\.?0+$/, "")
    .replace(".", ",");
}

/**
 * The dose as a cook reads it.
 *
 * Recipe lines carry a decimal quantity and a unit. The dish's own ingredient
 * list carries free text instead — "2 spicchi", "q.b." — which is passed
 * through as written, because rewriting someone's "q.b." into a number would be
 * inventing a recipe.
 */
export function formatDose(
  quantity: unknown,
  unit: string | null | undefined,
): string | null {
  if (quantity === null || quantity === undefined) return null;

  const raw = String(quantity).trim();
  if (raw === "") return null;

  const numeric = Number(raw);
  if (Number.isFinite(numeric)) {
    // A recorded zero is the venue saying "no amount", not "none of it".
    if (numeric === 0) return null;
    const amount = formatNumber(numeric);
    return unit ? `${amount} ${unit}` : amount;
  }

  // Free text: leave it exactly as the venue wrote it.
  return unit ? `${raw} ${unit}` : raw;
}

/** The name of a recipe line, by the precedence the backend already uses. */
function nameOfRecipeLine(item: any): { name: string; source: IngredientSource } | null {
  const candidates: Array<[unknown, IngredientSource]> = [
    [item?.catalogItem?.name, "catalog"],
    [item?.ingredient?.name, "warehouse"],
    [item?.recipe?.name, "preparation"],
    [item?.freeTextName, "free-text"],
  ];

  for (const [value, source] of candidates) {
    if (typeof value === "string" && value.trim() !== "") {
      return { name: value.trim(), source };
    }
  }
  return null;
}

function byDisplayOrder(a: any, b: any): number {
  return (Number(a?.displayOrder) || 0) - (Number(b?.displayOrder) || 0);
}

/**
 * Merge every road into one list, in the order the venue recorded them: the
 * recipe first, in its own order, then whatever hangs off the dish itself.
 */
export function buildDishIngredients(item: any): DishIngredients {
  const dish = item?.dish;
  const lines: DishIngredientLine[] = [];
  const seen = new Set<string>();

  const push = (name: string, dose: string | null, source: IngredientSource) => {
    const key = name.toLocaleLowerCase("it-IT");
    if (seen.has(key)) return; // first occurrence keeps its dose
    seen.add(key);
    lines.push({ name, dose, source });
  };

  const recipeItems: any[] = Array.isArray(dish?.recipe?.items) ? [...dish.recipe.items] : [];
  for (const recipeLine of recipeItems.sort(byDisplayOrder)) {
    const named = nameOfRecipeLine(recipeLine);
    if (!named) continue;
    push(named.name, formatDose(recipeLine?.quantity, recipeLine?.unit), named.source);
  }

  const dishIngredients: any[] = Array.isArray(dish?.ingredients) ? [...dish.ingredients] : [];
  for (const row of dishIngredients.sort(byDisplayOrder)) {
    const name = row?.ingredient?.name;
    if (typeof name !== "string" || name.trim() === "") continue;
    push(name.trim(), formatDose(row?.quantity, null), "dish");
  }

  if (lines.length === 0) return { kind: "nothing-recorded" };
  return { kind: "recorded", lines };
}
